/* eslint-disable react-refresh/only-export-components */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../u2000Core/components/dialog/U2000BasicDialog";
import U2000AutoCompleteInput from "../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBox from "../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000Label from "../../u2000Core/components/formControls/label/U2000Label";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type {
  U3129FindEmployeeItem,
  U3129InitLUEmployee,
} from "../U3129Service";
import { u3130FormatPeriodDateRange } from "../../u3130TimesheetApproval/tools/u3130FormatPeriodDateRange";
import { u3129BuildCreateTimesheetPeriodCandidates } from "../tools/u3129CreateTimesheetPeriodCandidates";
import { useU3129FindAvailablePeriodsHandlers, useU3129FindEmployeeHandlers } from "../U3129UseFindProjectSubprojectHandlers";
import { NS, U3129ROLES } from "../U3129";

/** @deprecated Préférer u3130FormatPeriodDateRange */
export const formatPeriodDateRange = u3130FormatPeriodDateRange;

export interface U3129AddTimesheetDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (periodDate: string, employee?: U3129FindEmployeeItem | null) => void;
  employee: U3129InitLUEmployee | null;
}

type PeriodOption = { value: string; label: string };

const U3129AddTimesheetDialog: React.FC<U3129AddTimesheetDialogProps> = ({ open, onClose, onConfirm, employee }) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const findEmployeeHandlers = useU3129FindEmployeeHandlers();
  const { fetchAvailablePeriods } = useU3129FindAvailablePeriodsHandlers();
  const canSelectOtherEmployee = hasRole(U3129ROLES.OTHER_EMPLOYEE);
  const candidatePeriods = useMemo(() => u3129BuildCreateTimesheetPeriodCandidates(), []);
  const [periodOptions, setPeriodOptions] = useState<PeriodOption[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedEmployee, setSelectedEmployee] = useState<U3129FindEmployeeItem | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedEmployee(
      employee
        ? {
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
          }
        : null,
    );
  }, [employee, open]);

  useEffect(() => {
    if (!open) {
      setPeriodOptions([]);
      setSelectedPeriod("");
      setPeriodsLoading(false);
      return;
    }
    if (!selectedEmployee?.id) {
      setPeriodOptions([]);
      setSelectedPeriod("");
      setPeriodsLoading(false);
      return;
    }

    let cancelled = false;
    setPeriodsLoading(true);
    fetchAvailablePeriods({
      employeeId: selectedEmployee.id,
      periods: candidatePeriods.map((p) => p.value),
    })
      .then((isos) => {
        if (cancelled) return;
        const opts: PeriodOption[] = isos.map((iso) => ({
          value: iso,
          label: u3130FormatPeriodDateRange(iso),
        }));
        setPeriodOptions(opts);
        setSelectedPeriod(opts[0]?.value ?? "");
      })
      .finally(() => {
        if (!cancelled) {
          setPeriodsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedEmployee?.id, candidatePeriods, fetchAvailablePeriods]);

  const employeeName = selectedEmployee ? [selectedEmployee.firstName, selectedEmployee.lastName].filter(Boolean).join(" ") : "—";

  const onSearchEmployee = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findEmployeeHandlers.search(criterias),
    [findEmployeeHandlers],
  );

  const displayEmployee = useCallback((item: Record<string, unknown>) => {
    const firstName = (item.firstName as string) ?? "";
    const lastName = (item.lastName as string) ?? "";
    return [firstName, lastName].filter(Boolean).join(" ");
  }, []);

  const extractDataForEmployee = useCallback((result: Record<string, unknown> | null) => {
    if (result) {
      return [
        {
          fieldName: "employee",
          value: {
            id: result.id as string,
            codeEmployee: result.codeEmployee as string | undefined,
            firstName: result.firstName as string | undefined,
            lastName: result.lastName as string | undefined,
          },
        },
      ];
    }
    return [{ fieldName: "employee", value: null }];
  }, []);

  const employeeFormStateHandlers = useMemo((): U2000FormStateHandlers<{ employee: U3129FindEmployeeItem | null }> => {
    const noop = () => {};
    return {
      getData: () => ({ employee: selectedEmployee }),
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => "EDIT",
      setEditMode: noop,
      setData: (data) => setSelectedEmployee((data as { employee?: U3129FindEmployeeItem | null } | null)?.employee ?? null),
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => "ADD",
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: noop,
      onBlur: noop,
      markTouched: noop,
    };
  }, [selectedEmployee]);

  const dialogFormStateHandlers = useMemo((): U2000FormStateHandlers<{ periodDate: string }> => {
    const noop = () => {};
    return {
      getData: () => ({ periodDate: selectedPeriod }),
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => "EDIT",
      setEditMode: noop,
      setData: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => "ADD",
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: (_e, value) => setSelectedPeriod(value != null ? String(value) : ""),
      onBlur: noop,
      markTouched: noop,
    };
  }, [selectedPeriod]);

  const periodComboDisabled =
    !selectedEmployee?.id || periodsLoading || periodOptions.length === 0;

  const periodHelperText = !selectedEmployee?.id
    ? t("msgPeriodSelectEmployeeFirst", { ns: NS, defaultValue: "Sélectionnez d'abord un employé." })
    : periodsLoading
      ? t("msgPeriodLoadingServer", { ns: NS, defaultValue: "Chargement des périodes disponibles…" })
      : periodOptions.length === 0
        ? t("msgNoPeriodsAvailableForEmployee", { ns: NS, defaultValue: "Aucune période disponible pour cet employé." })
        : null;

  const handleConfirm = () => {
    if (selectedPeriod && selectedEmployee?.id && !periodsLoading && periodOptions.some((o) => o.value === selectedPeriod)) {
      onConfirm(selectedPeriod, selectedEmployee);
      onClose();
    }
  };

  const handleClose = (reason: string) => {
    void reason;
    onClose();
  };

  return (
    <U2000BasicDialog
      isOpen={open}
      onClose={handleClose}
      title={t("dlgAddTimesheetTitle", { ns: NS, defaultValue: "Nouvelle feuille de temps" })}
      width="w-[500px]"
      contentOverflowVisible
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <div className="flex flex-col gap-1">
          <U2000Label label={t("lblEmployee", { ns: NS, defaultValue: "Employé" })} />
          {canSelectOtherEmployee ? (
            <U2000AutoCompleteInput
              formStateHandlers={employeeFormStateHandlers}
              fieldName="employee"
              mainFieldName="codeEmployee"
              entityLink="U3101"
              onSearch={onSearchEmployee}
              displayFn={displayEmployee}
              overrideExtractDataFn={extractDataForEmployee}
              minCarLaunchSearch={1}
              resultLabels={[]}
              resultFields={["codeEmployee", "firstName", "lastName"]}
              resultTableShowHeader={false}
            />
          ) : (
            <div className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900">{employeeName}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <U2000ComboBox<PeriodOption>
            formStateHandlers={dialogFormStateHandlers}
            fieldName="periodDate"
            label={t("lblPeriodDate", { ns: NS })}
            data={periodOptions}
            keyField="value"
            descField="label"
            getValue={(item) => item.value}
            formatLabel={(item) => item.label}
            findItemByValue={(value, data) => data.find((d) => d.value === value) ?? null}
            disabled={periodComboDisabled}
            placeholder={
              periodComboDisabled
                ? t("placeholderPeriodDisabled", { ns: NS, defaultValue: "—" })
                : undefined
            }
          />
          {periodHelperText ? <p className="text-xs text-gray-500">{periodHelperText}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => handleClose("cancel")}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
          >
            {t("btnCancel", { ns: NS, defaultValue: "Annuler" })}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={
              !selectedPeriod ||
              !selectedEmployee?.id ||
              periodsLoading ||
              !periodOptions.some((o) => o.value === selectedPeriod)
            }
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("btnAdd", { ns: NS })}
          </button>
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3129AddTimesheetDialog;
